import React from "react";
import NavBar from "../components/Navbar/NavBar";
import Footer from "../components/Footer";
import { useDocTitle } from "../components/CustomHook";
import { Link } from "react-router-dom";

/*
 * ZelTechnology Labs — what we're building and experimenting with.
 * Shows in-house creations (proof of skill, mission in action) and
 * honest "in the works" ideas. Nothing here claims availability that
 * doesn't exist.
 */

const projects = [
  {
    icon: "🦈",
    name: "LAMNA",
    status: "Preview live",
    desc: "Our own AI assistant platform — preview build is live for members, with the full agent fleet under development on dedicated AI infrastructure.",
    link: "/it",
    cta: "Try the preview",
  },
  {
    icon: "🎮",
    name: "ZelTechnology Arcade",
    status: "Live",
    desc: "PacZel and ZelSnake — built from scratch in-house, no third-party game code, with mobile controls. Proof that we build, not just consult.",
    link: "/games",
    cta: "Play now",
  },
  {
    icon: "🔓",
    name: "Open Finances",
    status: "Live",
    desc: "A nonprofit showing its real numbers publicly: contributions, spending, and allocation — updated from our records, always.",
    link: "/transparency",
    cta: "See the numbers",
  },
  {
    icon: "🚀",
    name: "Digital Growth",
    status: "Live",
    desc: "Our earned-income service: SEO and ad-platform onboarding for small businesses — free for fellow nonprofits. Paid work subsidizes the mission.",
    link: "/digital-growth",
    cta: "Learn more",
  },
];

const inTheWorks = [
  {
    icon: "🎁",
    title: "Hardware Assistance Program",
    desc: "Our most requested idea: a way for members to apply for free refurbished parts and vouchers toward laptops and desktops. In design now — see the plan and join the interest list.",
    link: "/hardware-assistance",
  },
  {
    icon: "🎫",
    title: "Member Support Tickets",
    desc: "A proper ticketing system so members get tracked, prioritized help instead of email threads.",
    link: "/contact",
  },
  {
    icon: "🤖",
    title: "Community Agent Fleet",
    desc: "Multiple AI agents, each tailored to a different part of our community — seniors, small businesses, students, veterans.",
    link: "/it",
  },
  {
    icon: "🏫",
    title: "Community Classes",
    desc: "Tech Basics, Cyber Safe, Inside a Computer, Build Your First Website, and AI Lab — shaped by community demand.",
    link: "/programs",
  },
];

const Labs = () => {
  useDocTitle("ZelT | ZelTechnology Labs");

  return (
    <div>
      <NavBar />
      <div className="mt-8 w-full bg-white py-12 lg:py-20">
        <div className="mx-4 max-w-6xl mx-auto">
          <div className="text-center mb-12" data-aos="zoom-in">
            <span className="inline-block px-4 py-1 rounded-full text-xs font-bold bg-teal-100 text-blue-900 border border-teal-400 mb-4">
              EXPERIMENTS · PROTOTYPES · PROOF
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-blue-900 mb-4">
              ZelTechnology Labs
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              See what we're building and experimenting with to make
              technology more accessible. Some of it is live, some is in the
              workshop — all of it is mission-driven and built in-house.
            </p>
          </div>

          {/* live projects */}
          <h2 className="text-2xl font-extrabold text-blue-900 mb-6" data-aos="fade-up">
            🔬 Currently in the Lab
          </h2>
          <div className="grid gap-6 md:grid-cols-2 mb-14">
            {projects.map((p) => (
              <div
                key={p.name}
                className="bg-gray-50 rounded-2xl p-6 shadow-lg border-t-4 border-teal-400 flex flex-col"
                data-aos="fade-up"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-3xl">{p.icon}</span>
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-teal-400 text-blue-900">
                    {p.status}
                  </span>
                </div>
                <h3 className="text-xl font-extrabold text-blue-900 mb-2">
                  {p.name}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed flex-1 mb-4">
                  {p.desc}
                </p>
                <Link
                  to={p.link}
                  className="self-start px-4 py-2 rounded-xl bg-blue-900 text-white text-sm font-bold hover:bg-blue-800 transition"
                >
                  {p.cta} →
                </Link>
              </div>
            ))}
          </div>

          {/* in the works */}
          <h2 className="text-2xl font-extrabold text-blue-900 mb-6" data-aos="fade-up">
            🛠️ On the Workbench
          </h2>
          <div className="grid gap-6 sm:grid-cols-2 mb-14">
            {inTheWorks.map((p) => (
              <Link
                key={p.title}
                to={p.link}
                className="group bg-white rounded-2xl p-6 shadow-md border-l-4 border-purple-300 hover:shadow-xl hover:border-purple-500 transition-all"
                data-aos="fade-up"
              >
                <p className="text-2xl mb-2">{p.icon}</p>
                <h3 className="font-extrabold text-blue-900 mb-1 group-hover:text-teal-500 transition-colors">
                  {p.title} →
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">{p.desc}</p>
              </Link>
            ))}
          </div>

          {/* philosophy */}
          <div
            className="rounded-2xl p-8 text-white text-center shadow-xl"
            style={{ background: "linear-gradient(90deg, #36395a, #1e3a8a)" }}
            data-aos="fade-up"
          >
            <p className="text-lg md:text-xl font-semibold max-w-3xl mx-auto leading-relaxed">
              "We believe in showing our work. Everything in this Lab was{" "}
              <span className="text-teal-300">built in-house, by hand</span> —
              because a nonprofit that teaches technology should demonstrate
              it, not just talk about it."
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Labs;
